import {
  Direction,
  Grid,
  Side,
  type BlotterTableRow,
  type CareOrder,
  type SideEnum,
  type UseBlotterTable,
} from '@talos/kyoko';

import { KPI, KPITypes } from '@src/components/KPI';
import styled from 'styled-components';
import { useCareOrderHUDData } from './useCareOrderHUDData';

export interface CareOrderBlotterHUDProps<TData> {
  blotterTable: UseBlotterTable<TData>;
  getCareOrder: (row: BlotterTableRow<TData>) => CareOrder | undefined;
}

export function CareOrderBlotterHUD<TData>({ blotterTable, getCareOrder }: CareOrderBlotterHUDProps<TData>) {
  const {
    totalNotionalBuy,
    notionalBuyExecuted,
    notionalBuyOutstanding,
    totalNotionalSell,
    notionalSellExecuted,
    notionalSellOutstanding,
    isLoading,
  } = useCareOrderHUDData(blotterTable, getCareOrder);

  return (
    <Wrapper>
      <KPI
        label={<SidedLabel side="Buy" text="Total Notional" />}
        isLoading={isLoading}
        tooltip="Including filled and open quantity, but excluding canceled quantity."
        positiveDirection={Direction.Asc}
        value={totalNotionalBuy}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
      <KPI
        label={<SidedLabel side="Buy" text="Notional Executed" />}
        isLoading={isLoading}
        positiveDirection={Direction.Asc}
        value={notionalBuyExecuted}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
      <KPI
        label={<SidedLabel side="Buy" text="Notional Outstanding" />}
        isLoading={isLoading}
        positiveDirection={Direction.Asc}
        value={notionalBuyOutstanding}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
      <KPI
        label={<SidedLabel side="Sell" text="Total Notional" />}
        tooltip="Including filled and open quantity, but excluding canceled quantity."
        isLoading={isLoading}
        positiveDirection={Direction.Asc}
        value={totalNotionalSell}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
      <KPI
        label={<SidedLabel side="Sell" text="Notional Executed" />}
        isLoading={isLoading}
        positiveDirection={Direction.Asc}
        value={notionalSellExecuted}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
      <KPI
        label={<SidedLabel side="Sell" text="Notional Outstanding" />}
        isLoading={isLoading}
        positiveDirection={Direction.Asc}
        value={notionalSellOutstanding}
        type={KPITypes.Currency}
        currency="USD"
        isEstimate={true}
      />
    </Wrapper>
  );
}

const SidedLabel = ({ side, text }: { side: SideEnum; text: string }) => {
  return (
    <>
      <Side side={side} fontWeight="500">
        {side}
      </Side>{' '}
      - {text}
    </>
  );
};

const Wrapper = styled(Grid)`
  overflow-x: hidden;
  gap: calc(var(--spacing2) * 1px);
  margin-top: calc(var(--spacing2) * 1px);
  margin-bottom: calc(var(--spacing2) * 1px);
  min-height: 5.125rem;
  flex-wrap: wrap;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));

  @media screen and (max-width: 1000px) {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
`;
